import {
  normalizeProximityPreset,
  proximityBandsFor,
  type ProximityPreset,
} from '../../../packages/putt-scene/src/proximityTarget';
import type { TvGame, TvHudTone, TvNotice, TvPuttEvent, TvRaceBar, TvSnapshot, TvSummary } from './protocol';
import { gradeStartLine } from './startLineBands';
import { formatDistance, formatLeave, formatSpeed, readoutSpeedMps } from './units';

const KNOWN_GAME_KINDS = new Set<TvGame['kind']>([
  'target',
  'distance',
  'sim9',
  'lineFour',
  'turfWar',
  'baseball',
  'football',
  'cricket',
  'poker',
  'startLine',
  'ladder',
  'event',
]);

/** Firestore is a runtime boundary. A newer phone may publish a game kind an
 * older TV bundle has never seen; treating every unknown object as Poker
 * would dereference fields that are not there and blank the primary display. */
export function isKnownTvGame(value: unknown): value is TvGame {
  if (!value || typeof value !== 'object') return false;
  const kind = (value as { kind?: unknown }).kind;
  return typeof kind === 'string' && KNOWN_GAME_KINDS.has(kind as TvGame['kind']);
}

/** The app's explicit make verdict wins over every receiver heuristic. The
 * proximity fallback exists only for older events that do not carry one. */
export function formatTvProximity(value: number | null, holed?: boolean): string {
  if (holed === true) return 'IN 👑';
  if (value == null || !Number.isFinite(value)) return '—';
  if (holed === false) return formatLeave(value);
  return Math.abs(value) <= 0.35 ? 'IN 👑' : formatLeave(value);
}

export interface TvHandoff {
  gameKind: 'lineFour' | 'turfWar';
  playerIndex: 0 | 1;
  playerName: string;
}

/** Line Four and Turf War keep the 3D scene mounted between turns. Mirror the
 * phone's three-second handoff beat on the primary display. */
export function tvHandoffForGame(game: TvGame): TvHandoff | null {
  if ((game.kind !== 'lineFour' && game.kind !== 'turfWar') || game.phase !== 'handoff') {
    return null;
  }
  const playerIndex = game.activePlayerIndex;
  const playerName = game.names?.[playerIndex];
  if (typeof playerName !== 'string' || playerName.trim().length === 0) return null;
  return { gameKind: game.kind, playerIndex, playerName };
}

export interface TvTurfWarStanding {
  zoneIndex: number;
  counts: [number, number];
  controllerIndex: 0 | 1 | null;
}

/** Prefer the phone engine's authoritative ledger. The fallback mirrors its
 * sudden-death filtering for an older paired app that publishes only shots:
 * current-round attempts remain provisional until both players answer, and
 * misses outside the assigned playoff zone never change territory control. */
export function tvTurfWarStandings(
  game: Extract<TvGame, { kind: 'turfWar' }>,
): TvTurfWarStanding[] {
  const published = Array.isArray(game.standings) ? game.standings : [];
  if (published.length > 0) {
    return [0, 1, 2, 3, 4].map((zoneIndex) => {
      const standing = published.find((entry) => entry?.zoneIndex === zoneIndex);
      const counts: [number, number] = [
        finiteCount(standing?.counts?.[0]),
        finiteCount(standing?.counts?.[1]),
      ];
      return {
        zoneIndex,
        counts,
        controllerIndex: counts[0] === counts[1] ? null : counts[0] > counts[1] ? 0 : 1,
      };
    });
  }

  const playoff = game.state.suddenDeath;
  const scoringShots = (game.state.shots ?? []).filter((shot) => {
    if (shot.phase !== 'suddenDeath') return true;
    if (!playoff || shot.zoneIndex !== playoff.zoneIndex) return false;
    if (game.state.phase === 'complete') return true;
    return shot.suddenDeathRound != null && shot.suddenDeathRound < playoff.round;
  });

  return [0, 1, 2, 3, 4].map((zoneIndex) => {
    const counts: [number, number] = [0, 0];
    for (const shot of scoringShots) {
      if (shot.zoneIndex === zoneIndex && (shot.playerIndex === 0 || shot.playerIndex === 1)) {
        counts[shot.playerIndex] += 1;
      }
    }
    return {
      zoneIndex,
      counts,
      controllerIndex: counts[0] === counts[1] ? null : counts[0] > counts[1] ? 0 : 1,
    };
  });
}

function finiteCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : 0;
}

/* ---- Ghost putts (2026-09-25 mode map) ---- */

/** A recorded opponent's putt replayed on the TV. It rolls, but it is not
 * the phone's putt: no strip, no score, no tally. */
export function isGhostPutt(event: TvPuttEvent | null | undefined): boolean {
  return event?.kind === 'putt' && event.ghost === true;
}

/** The phone's own putt, or null when the current event is a ghost. Every
 * "your last putt" readout keys on this, never on the raw event. */
export function ownPutt(event: TvPuttEvent | null | undefined): TvPuttEvent | null {
  if (!event || event.kind !== 'putt') return null;
  return event.ghost === true ? null : event;
}

/** What the ghost chip over the stage says. */
export function ghostPuttLabel(event: TvPuttEvent): { name: string; color: string } {
  const name = event.playerName?.trim();
  return {
    name: name && name.length > 0 ? name : 'OPPONENT',
    color: event.color?.trim() || '#4fc3f7',
  };
}

/* ---- Countdown rings ---- */

/** Longest countdown the page will draw. A phone clock far ahead of the TV's
 * cannot stretch a five-second ring into a minute. */
export const MAX_COUNTDOWN_MS = 30_000;

export interface CountdownProgress {
  /** Milliseconds left, clamped to [0, duration]. */
  remainingMs: number;
  /** Ring fill remaining, 1 at the start and 0 at `endsAtMs`. */
  fraction: number;
  /** Whole seconds left, rounded up so the ring never shows 0 while filled. */
  seconds: number;
  done: boolean;
}

/**
 * The protocol carries only the end time, so the ring's full length is
 * measured from the moment the TV first saw that end time. Clock skew is
 * tolerated by clamping: an end time already past reads as done, an end time
 * absurdly far ahead is capped at MAX_COUNTDOWN_MS.
 */
export function countdownProgress(
  endsAtMs: number,
  firstSeenAtMs: number,
  nowMs: number,
): CountdownProgress {
  const durationMs = Math.max(0, Math.min(MAX_COUNTDOWN_MS, endsAtMs - firstSeenAtMs));
  const remainingMs = Math.max(0, Math.min(durationMs, endsAtMs - nowMs));
  const fraction = durationMs > 0 ? remainingMs / durationMs : 0;
  return {
    remainingMs,
    fraction,
    seconds: Math.ceil(remainingMs / 1000),
    done: remainingMs <= 0,
  };
}

/* ---- Distance rounds ---- */

/** The chip naming another player's ghost balls (the Daily's #1,
 * 2026-10-20), or null unless both fields are non-empty strings. */
export function ghostLegendOf(value: unknown): { label: string; color: string } | null {
  if (!value || typeof value !== 'object') return null;
  const { label, color } = value as { label?: unknown; color?: unknown };
  return typeof label === 'string' && label && typeof color === 'string' && color ? { label, color } : null;
}

/** Progress boxes for a distance round: the leaves so far padded to the
 * round length. `concealNewest` blanks the latest leave while its ball is
 * still rolling on the TV. */
export function distanceRoundBoxes(
  leavesFt: Array<number | null> | null | undefined,
  puttsTotal: number,
  concealNewest = false,
): Array<number | null> {
  const total = Math.max(1, Math.min(20, Math.floor(puttsTotal) || 5));
  const next = [...(leavesFt ?? [])].slice(0, total);
  while (next.length < total) next.push(null);
  if (concealNewest) {
    for (let index = next.length - 1; index >= 0; index -= 1) {
      if (next[index] != null) {
        next[index] = null;
        break;
      }
    }
  }
  return next;
}

/* ---- The race ("bullet") bar: a port of the phone's KingRaceBars ---- */

/**
 * The phone's single bullet-style race bar (app/LagKing/src/components/
 * KingRaceBars.tsx), the one bar every race mode draws: King of the Hill,
 * the daily-leader race, the friend "score to beat" race, and the Pass &
 * Play leader chase. Cole, 2026-09-25: "I dont want to reinvent the bullet
 * bar." This model reproduces the component's math from the fields the app
 * publishes; BulletBar.tsx draws it.
 *
 * - SHARED SCALE: the full track is the reference round's TOTAL (every mode,
 *   not only KOTH). Your fill grows at the same ft per pixel.
 * - Each segment is green or red by THAT putt against the reference's
 *   same-numbered putt; a later putt never recolors it.
 * - The gold crown marker is the pace: the reference's total through your
 *   putt count. The running total left of the bar is green at or under pace,
 *   red above it.
 * - Past the reference total the race is unwinnable: fill and total gray out.
 */
export type BulletSegmentTone = 'won' | 'lost' | 'out';

export interface BulletBarModel {
  /** Always "YOU", as every phone host passes. */
  playerName: string;
  playerTotalText: string;
  totalTone: 'ahead' | 'behind' | 'out';
  /** Right of the bar: the reference total to beat, "13.8 FT". */
  referenceTotalLabel: string;
  /** One per played putt, in order; fraction of the full track (may pass 1;
   *  the track clips, as the phone clamps to the remaining width). */
  segments: Array<{ fraction: number; tone: BulletSegmentTone }>;
  /** Crown pace marker center as a fraction of the track, 0..1. */
  paceFraction: number;
  /** Friend race only: "Score to beat: @handle" above the bar. */
  caption: string | null;
  /** Friend race only: the chase goal under the bar. */
  goal: string | null;
  /** Reference name, for the accessible label. */
  referenceName: string;
}

const BULLET_EPS = 1e-9;

/** The phone's reference leaves: the published round, or (legacy KOTH crowns
 *  and older app builds without leaves) the average split evenly across the
 *  round, the same equal-fifths fallback KothRoundScreen uses. */
function bulletReferenceLeaves(race: TvRaceBar, puttsTotal: number): number[] | null {
  const leaves = race.theirLeavesFt;
  if (leaves && leaves.length > 0) {
    return leaves.map((value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0));
  }
  const avg = finiteOrNull(race.theirAvgFt);
  if (avg === null) return null;
  const count = Math.max(1, Math.floor(puttsTotal) || 5);
  return Array.from({ length: count }, () => avg);
}

/**
 * Port of the app's distanceChaseGoal (screens/friendChallenges/
 * localMultiplayer/leaderChase.ts), the friend race's goal line under the
 * bar. Five putts per round, rounded DOWN so it never advertises a losing
 * threshold.
 */
export function distanceChaseGoal(leaderTotal: number, leaves: number[]): string {
  const total = leaves.reduce((sum, n) => sum + n, 0);
  const remaining = Math.max(0, 5 - leaves.length);
  const gap = leaderTotal - total;
  const tolerance = 5e-6;
  if (!remaining) return Math.abs(gap) < tolerance ? 'Tied for the lead' : gap > 0 ? 'New leader' : 'Round complete';
  if (gap <= -tolerance) return `Leader out of reach · ${remaining} putts left`;
  if (gap < tolerance) return `Hole all ${remaining} to tie`;
  const needed = Math.floor((gap - tolerance) / remaining * 100) / 100;
  return needed > 0
    ? `Under ${needed.toFixed(2)} ft avg to lead · ${remaining} left`
    : `Hole all ${remaining} to lead`;
}

/**
 * The bullet bar for a distance round, or null where the phone draws none.
 * `concealNewest` holds the pre-roll frame while the ball rolls, as
 * PuttScene holds its race bar (the newest segment and the total would
 * announce the leave early).
 */
export function bulletBarModel(
  game: Extract<TvGame, { kind: 'distance' }>,
  options: { concealNewest?: boolean } = {},
): BulletBarModel | null {
  const race = game.race;
  if (!race) return null;
  const referenceLeaves = bulletReferenceLeaves(race, game.puttsTotal);
  if (!referenceLeaves || referenceLeaves.length === 0) return null;
  const refTotal = referenceLeaves.reduce((sum, value) => sum + Math.max(0, value), 0);
  const playerLeaves = distanceRoundBoxes(
    game.leavesFt,
    Math.max(game.puttsTotal, referenceLeaves.length),
    options.concealNewest ?? false,
  );
  const played = playerLeaves.filter((value): value is number => value != null);

  // Per-mode host props: friend adds a caption and goal; daily and local
  // show a perfect (0.0) reference; KOTH hides the bar for a 0 ft King.
  const isFriend = game.mode === 'friend';
  const goal = isFriend ? game.distanceGoal ?? distanceChaseGoal(refTotal, played.map((value) => Math.max(0, value))) : null;
  const caption = isFriend ? `Score to beat: ${race.label.startsWith('@') ? race.label : 'Challenger'}` : null;
  const showZeroReference = game.mode === 'daily' || game.mode === 'local';
  if (!goal && !showZeroReference && !(refTotal > 0)) return null;

  const scaleTotal = Math.max(refTotal, 0.001);
  const paceFt = referenceLeaves.slice(0, played.length).reduce((sum, value) => sum + Math.max(0, value), 0);
  const playerTotal = played.reduce((sum, value) => sum + Math.max(0, value), 0);
  const out = playerTotal > refTotal + BULLET_EPS;
  const behind = playerTotal > paceFt + BULLET_EPS;
  const segments: BulletBarModel['segments'] = [];
  playerLeaves.forEach((leave, index) => {
    if (leave == null) return;
    const refLeaf = referenceLeaves[index];
    const won = refLeaf === undefined || Math.max(0, leave) <= Math.max(0, refLeaf) + BULLET_EPS;
    segments.push({ fraction: Math.max(0, leave) / scaleTotal, tone: out ? 'out' : won ? 'won' : 'lost' });
  });
  return {
    playerName: 'YOU',
    playerTotalText: playerTotal.toFixed(1),
    totalTone: out ? 'out' : behind ? 'behind' : 'ahead',
    referenceTotalLabel: `${refTotal.toFixed(1)} FT`,
    segments,
    paceFraction: Math.min(1, paceFt / scaleTotal),
    caption,
    goal,
    referenceName: race.label,
  };
}

/** The event round's race bar, through the Distance model: the leader's
 *  round is the track, the entrant's name sits where YOU would. */
export function eventBulletBarModel(
  game: Extract<TvGame, { kind: 'event' }>,
  options: { concealNewest?: boolean } = {},
): BulletBarModel | null {
  if (!game.race || !game.leavesFt) return null;
  const model = bulletBarModel(
    {
      kind: 'distance',
      mode: 'local',
      targetFt: game.targetFt,
      leavesFt: game.leavesFt,
      puttsTotal: game.puttsTotal ?? game.leavesFt.length,
      avgProxFt: null,
      race: game.race,
    },
    options,
  );
  if (!model) return null;
  const name = (game.playerName ?? '').trim();
  return { ...model, playerName: name ? name.toUpperCase() : model.playerName };
}

export function distanceModeTitle(mode: Extract<TvGame, { kind: 'distance' }>['mode']): string {
  switch (mode) {
    case 'daily': return "TODAY'S CHALLENGE";
    case 'friend': return 'FRIEND CHALLENGE';
    case 'local': return 'PASS & PLAY';
    case 'koth': return 'KING OF THE HILL';
    default: return 'DISTANCE CHALLENGE';
  }
}

/* ---- Start Line practice ---- */

export interface StartLineHero {
  grade: 'PURE' | 'SLIGHT' | 'WIDE' | 'MAJOR' | 'WAITING';
  direction: 'left' | 'right' | null;
  tone: 'success' | 'gold' | 'danger' | 'neutral';
}

/** The practice grade hero. The phone's published grade wins; the putt's
 * angle fills in for an older app that publishes only the event. */
export function startLineHero(
  game: Pick<Extract<TvGame, { kind: 'startLine' }>, 'grade' | 'direction'>,
  angleDeg: number | null | undefined,
): StartLineHero {
  const published = game.grade?.trim().toUpperCase();
  let grade: StartLineHero['grade'] | null = null;
  if (published === 'PURE' || published === 'SLIGHT' || published === 'WIDE' || published === 'MAJOR') {
    grade = published;
  }
  let direction: 'left' | 'right' | null = game.direction ?? null;
  if (grade == null && angleDeg != null && Number.isFinite(angleDeg)) {
    const verdict = gradeStartLine(angleDeg);
    grade = verdict.grade === 'pure' ? 'PURE' : verdict.grade === 'good' ? 'SLIGHT' : verdict.grade === 'off' ? 'WIDE' : 'MAJOR';
    direction = direction ?? verdict.direction;
  }
  if (grade == null) return { grade: 'WAITING', direction: null, tone: 'neutral' };
  if (grade === 'PURE') direction = null;
  const tone = grade === 'PURE' || grade === 'SLIGHT' ? 'success' : grade === 'WIDE' ? 'gold' : 'danger';
  return { grade, direction, tone };
}

function finiteOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? Math.abs(value) : null;
}

/* ---- Board cards and mirror layout ---- */

/** Rows shown on a leaderboard card before the viewer's own row is pinned. */
export const BOARD_TOP_ROWS = 10;

export type TvBoardRow = NonNullable<TvSummary['board']>['rows'][number];

/** Top ten in rank order; the viewer's row is appended when it ranks lower. */
export function boardRowsForDisplay(rows: TvBoardRow[]): Array<TvBoardRow & { pinned?: boolean }> {
  const ordered = [...rows].sort((a, b) => a.rank - b.rank);
  const top = ordered.slice(0, BOARD_TOP_ROWS);
  const you = ordered.find((row) => row.you);
  if (you && !top.includes(you)) return [...top, { ...you, pinned: true }];
  return top;
}

/** More options than this and the mirror card lays them out as mode tiles
 * (the Challenge home) instead of a single row of choices. */
export const MIRROR_GRID_THRESHOLD = 6;

export function mirrorLayout(optionCount: number): 'list' | 'grid' {
  return optionCount > MIRROR_GRID_THRESHOLD ? 'grid' : 'list';
}

/* ---------------------------------------------------------------------------
 * Interrupts (TV_REVIVAL_PLAN.md Part 2, "Interrupts, on every state"). The
 * phone publishes `snapshot.notice`; the TV says it out loud on whatever it
 * is showing and never lets it change the presentation kind.
 * ------------------------------------------------------------------------ */

/** A notice with `expiresAtMs` hides itself locally at that time (a read
 * error clears after 3 s without another publish). */
export function noticeVisible(notice: TvNotice | null | undefined, nowMs: number): boolean {
  if (!notice || typeof notice.text !== 'string' || notice.text.trim().length === 0) return false;
  const expiresAtMs = notice.expiresAtMs;
  if (expiresAtMs == null) return true;
  return Number.isFinite(expiresAtMs) && nowMs < expiresAtMs;
}

/** Tone for the strip: the phone's tone wins, else one per kind. */
export function noticeTone(notice: TvNotice): TvHudTone {
  if (notice.tone) return notice.tone;
  switch (notice.kind) {
    case 'gateDisconnected':
    case 'gateHit':
    case 'forfeit':
      return 'danger';
    case 'info':
      return 'neutral';
    default:
      return 'gold';
  }
}

export type TvNoticeGlyph = 'moon' | 'unplugged' | 'impact' | 'retry' | 'check' | 'update' | 'battery' | 'people' | 'flag' | 'info';

/** Icon by kind, drawn by StatusHints. */
export function noticeGlyph(kind: TvNotice['kind']): TvNoticeGlyph {
  switch (kind) {
    case 'gateAsleep': return 'moon';
    case 'gateDisconnected': return 'unplugged';
    case 'gateHit': return 'impact';
    case 'readError': return 'retry';
    case 'confirm': return 'check';
    case 'firmware': return 'update';
    case 'battery': return 'battery';
    case 'players': return 'people';
    case 'forfeit': return 'flag';
    default: return 'info';
  }
}

export interface PhoneEmphasis {
  before: string;
  phrase: string;
  after: string;
}

/** Splits "on your device" (or an older phone's "on your phone") out of a
 * notice line so the strip can emphasize it. Null when the line does not say
 * it (the strip then adds the phrase). */
export function splitPhoneEmphasis(text: string): PhoneEmphasis | null {
  const match = /on your (?:device|phone)/i.exec(text);
  if (!match) return null;
  return {
    before: text.slice(0, match.index),
    phrase: match[0],
    after: text.slice(match.index + match[0].length),
  };
}

export type TvGatePillState = 'connected' | 'disconnected' | 'asleep' | 'battery' | 'phone';

export interface TvGatePill {
  state: TvGatePillState;
  label: string;
}

/**
 * The gate pill on every presentation. A gate notice overrides the plain
 * connected flag: asleep (amber), disconnected (red), low battery (a battery
 * glyph). Nothing is shown until the phone has reported the gate or a gate
 * notice. `notice` is the visible notice (already through `noticeVisible`).
 */
export function gatePill(snapshot: TvSnapshot | null | undefined, notice: TvNotice | null | undefined = snapshot?.notice): TvGatePill | null {
  if (notice) {
    if (notice.kind === 'gateAsleep') return { state: 'asleep', label: 'GATE ASLEEP' };
    if (notice.kind === 'gateDisconnected') return { state: 'disconnected', label: 'GATE NOT CONNECTED' };
    if (notice.kind === 'battery') return { state: 'battery', label: 'LOW BATTERY' };
  }
  if (snapshot?.gateConnected == null) return null;
  return snapshot.gateConnected === true
    ? { state: 'connected', label: 'GATE CONNECTED' }
    : { state: 'disconnected', label: 'GATE NOT CONNECTED' };
}

/* ---------------------------------------------------------------------------
 * The per-putt data strip (Cole, 2026-09-25: "Phone should contain the button
 * clicks. TV should contain the details."). The phone's PuttDataStrip
 * (app/LagKing/src/components/PuttDataStrip.tsx) is SPEED, START LINE,
 * ROLLED, LEAVE; the TV draws the same four cells after every putt in every
 * mode, from the putt event alone.
 * ------------------------------------------------------------------------ */

/** The phone's short start-line label (utils/startLine.ts CATEGORY_LABEL),
 * upper-cased the way its HUDs show it. Same band edges as startLineBands. */
export function formatStartLine(angleDeg: number | null | undefined): string {
  if (angleDeg == null || !Number.isFinite(angleDeg)) return '—';
  const verdict = gradeStartLine(angleDeg);
  if (verdict.grade === 'pure') return 'PURE 👑';
  const left = verdict.direction === 'left';
  if (verdict.grade === 'good') return left ? '<- SLIGHT' : 'SLIGHT ->';
  if (verdict.grade === 'off') return left ? '<-- WIDE' : 'WIDE -->';
  return left ? '<--- MAJOR' : 'MAJOR --->';
}

/** The phone's startLineToneKey: PURE and SLIGHT green, WIDE gold, MAJOR red. */
export function startLineTone(angleDeg: number | null | undefined): TvHudTone {
  if (angleDeg == null || !Number.isFinite(angleDeg)) return 'neutral';
  const grade = gradeStartLine(angleDeg).grade;
  if (grade === 'pure' || grade === 'good') return 'success';
  if (grade === 'off') return 'gold';
  return 'danger';
}

/**
 * The START LINE cell of the Target and Distance HUDs. Start line, like
 * speed, is known at contact, so it shows the instant the putt event lands,
 * never at settle: the phone's 3D rail does the same (PuttScene startLineLabel
 * shows while `active`). Only ROLLED and LEAVE wait for the ball to stop
 * (Cole, 2026-09-30: "start line should show up instantly like speed does").
 * A low-confidence read carries the phone's "~".
 */
export function contactStartLine(
  event: Pick<TvPuttEvent, 'angleDeg' | 'angleLowConfidence'> | null | undefined,
): { value: string; tone: TvHudTone | undefined } {
  const angle = event?.angleDeg;
  if (!event || angle == null || !Number.isFinite(angle)) return { value: '—', tone: undefined };
  return {
    value: `${event.angleLowConfidence ? '~' : ''}${formatStartLine(angle)}`,
    tone: startLineTone(angle),
  };
}

/** Leave color by the phone's proximity target: the same continuous edges
 * as its proximityBandColor (packages/putt-scene/src/proximityTarget.ts), at
 * the preset the phone publishes on the scene (default 8% / 16%). */
export function proximityTone(
  value: number | null | undefined,
  targetFt: number,
  preset?: ProximityPreset | null,
): TvHudTone {
  if (value == null || !Number.isFinite(value)) return 'neutral';
  const magnitude = Math.abs(value);
  const { goodMaxFt, midMaxFt } = proximityBandsFor(targetFt, normalizeProximityPreset(preset));
  if (magnitude <= goodMaxFt) return 'success';
  if (magnitude <= midMaxFt) return 'gold';
  return 'danger';
}

/** A putt's make verdict: the app's explicit one (`hasHoled`; the app always
 * sends `holed`, false when it has no verdict), else the proximity heuristic
 * formatTvProximity uses. */
export function puttMade(event: Pick<TvPuttEvent, 'hasHoled' | 'holed' | 'proximityFt'>): boolean {
  if (event.hasHoled === true) return event.holed === true;
  return event.proximityFt != null && Number.isFinite(event.proximityFt) && Math.abs(event.proximityFt) <= 0.35;
}

export interface PuttDataCell {
  key: 'speed' | 'line' | 'roll' | 'leave';
  label: string;
  value: string;
  tone?: TvHudTone;
}

export interface PuttDataOptions {
  /** False while the ball is still rolling: ROLLED and LEAVE hold at a dash
   * (the phone's revealLeaveAtSettle). Speed and start line are known at
   * contact and show at once. */
  revealed: boolean;
  /** Binary make/miss modes (Start Line streak, Line Four, Turf War, Ladder)
   * publish `hideProxLabel`; a leave distance is noise there. */
  showLeave?: boolean;
  /** Tone the leave by the distance bands of this target. */
  targetFt?: number | null;
  /** The phone's proximity target (scene.proximityPreset). */
  proximityPreset?: ProximityPreset | null;
  /** Also describe a ghost (an opponent's recorded putt): the Sim 9 match
   * response screen shows the strip for their putt while it replays. */
  includeGhost?: boolean;
  /** Hold START LINE at a dash until the ball stops, too: Darts-Cricket's
   * line is the multiplier, revealed with the zone (2026-10-20). */
  lineAfterReveal?: boolean;
}

/** The four cells of the strip, or null with no putt (or a ghost putt). */
export function puttDataStrip(
  event: TvPuttEvent | null | undefined,
  options: PuttDataOptions,
): PuttDataCell[] | null {
  const own = options.includeGhost && event?.kind === 'putt' ? event : ownPutt(event);
  if (!own) return null;
  const angle = own.angleDeg;
  const angleKnown = angle != null && Number.isFinite(angle) && (options.revealed || !options.lineAfterReveal);
  const cells: PuttDataCell[] = [
    {
      key: 'speed',
      label: 'BALL SPEED',
      value: Number.isFinite(own.speedMps) && own.speedMps > 0 ? formatSpeed(readoutSpeedMps(own)) : '—',
    },
    {
      key: 'line',
      label: 'START LINE',
      value: angleKnown ? `${own.angleLowConfidence ? '~' : ''}${formatStartLine(angle)}` : '—',
      tone: angleKnown ? startLineTone(angle) : 'neutral',
    },
    {
      key: 'roll',
      label: 'ROLLED',
      value: options.revealed && Number.isFinite(own.distanceFt) ? formatDistance(Math.abs(own.distanceFt)) : '—',
    },
  ];
  if (options.showLeave !== false) {
    const made = puttMade(own);
    const leave = own.proximityFt;
    cells.push({
      key: 'leave',
      label: 'LEAVE',
      value: !options.revealed
        ? '—'
        : made
          ? 'IN 👑'
          : leave != null && Number.isFinite(leave)
            ? formatLeave(leave)
            : '—',
      tone: !options.revealed
        ? undefined
        : made
          ? 'success'
          : leave != null && Number.isFinite(leave) && options.targetFt != null && options.targetFt > 0
            ? proximityTone(leave, options.targetFt, options.proximityPreset)
            : undefined,
    });
  }
  return cells;
}

/* ---- King of the Hill: a race on total proximity ---- */

/** Sum of the leaves so far; null before the first. */
export function distanceTotalFt(leavesFt: Array<number | null> | null | undefined): number | null {
  const finite = (leavesFt ?? []).filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  return finite.length ? finite.reduce((sum, value) => sum + Math.abs(value), 0) : null;
}

/* ---- Putting Ladder opening prompt ---- */

export interface LadderPrompt {
  title: string;
  detail: string | null;
  tryAgain: boolean;
}

/**
 * LadderGreenLandscape's opening card: "Hit a 15 foot putt to start the
 * ladder", or after a short opening "Try Again · Last attempt: 12.4 ft, need
 * 15 ft". The try-again fields are a publishing gap; without them the TV
 * shows the first prompt through the whole opening.
 */
export function ladderOpeningPrompt(game: Extract<TvGame, { kind: 'ladder' }>): LadderPrompt | null {
  if (game.variant !== 'solo' || game.phase !== 'opening') return null;
  const low = Number.isFinite(game.lowFt) ? Math.round(game.lowFt) : null;
  const need = low == null ? 'the red line' : `${low} FT`;
  if (game.openingPrompt === 'tryAgain') {
    const last = game.lastShortOpeningFt;
    return {
      title: 'TRY AGAIN',
      detail: last != null && Number.isFinite(last)
        ? `LAST ATTEMPT ${formatDistance(Math.abs(last))} · NEED ${need}`
        : `HIT A ${need} PUTT TO START THE LADDER`,
      tryAgain: true,
    };
  }
  return { title: `HIT A ${need} PUTT`, detail: 'TO START THE LADDER', tryAgain: false };
}
